import React, { useState, useEffect } from 'react';
import { 
  MessageSquare, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Mail,
  Phone,
  Building,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Star,
  Reply,
  CheckSquare,
  Square,
  Zap,
  Loader2
} from 'lucide-react';
import { contactAPI } from '../../services/api';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const ContactsManagement = () => {
  const { notify } = useNotificationHook();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterService, setFilterService] = useState('all');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [contactsPerPage] = useState(10);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Bulk actions state
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Fetch contacts from backend (make this a named function so it can be reused)
  const fetchContacts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await contactAPI.getInquiries({ page: currentPage, limit: contactsPerPage });
      setContacts(res.data.data.inquiries || []);
    } catch (err) {
      setError('Failed to load messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line
  }, [currentPage]);

  const totalContacts = contacts.length;
  const totalPages = Math.ceil(totalContacts / contactsPerPage);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleStatusUpdate = async (contactId, newStatus) => {
    try {
      setLoading(true);
      const contact = contacts.find(c => c.id === contactId);
      // Default to 'medium' if priority is missing or invalid
      const validPriority = ['low', 'medium', 'high', 'urgent'].includes(contact.priority) ? contact.priority : 'medium';
      const response = await contactAPI.updateInquiry(contactId, {
        status: newStatus,
        priority: validPriority
      });
      if (response.data && response.data.success) {
        await fetchContacts();
      } else {
        setError('Failed to update status.');
      }
    } catch (err) {
      setError('Failed to update status.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        setLoading(true);
        await contactAPI.deleteInquiry(contactId);
        setContacts(contacts.filter(contact => contact.id !== contactId));
      } catch (err) {
        setError('Failed to delete message.');
      } finally {
        setLoading(false);
      }
    }
  };

  // Add a handler for updating priority
  const handlePriorityUpdate = async (contactId, newPriority) => {
    try {
      setLoading(true);
      const contact = contacts.find(c => c.id === contactId);
      const response = await contactAPI.updateInquiry(contactId, {
        priority: newPriority,
        status: contact.status
      });
      if (response.data && response.data.success) {
        await fetchContacts();
      } else {
        setError('Failed to update priority.');
      }
    } catch (err) {
      setError('Failed to update priority.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (status) {
      case 'new':
        return `${baseClasses} text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20`;
      case 'in_progress':
        return `${baseClasses} text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20`;
      case 'resolved':
        return `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`;
      case 'closed':
        return `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  const getPriorityBadge = (priority) => {
    const baseClasses = "px-2 py-1 text-xs font-medium rounded-full";
    switch (priority) {
      case 'high':
        return `${baseClasses} text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20`;
      case 'medium':
        return `${baseClasses} text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20`;
      case 'low':
        return `${baseClasses} text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20`;
      default:
        return `${baseClasses} text-gray-700 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20`;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'new':
        return <AlertCircle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'resolved':
        return <CheckCircle className="h-4 w-4" />;
      case 'closed':
        return <XCircle className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  // Filter contacts based on search and filters
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || contact.status === filterStatus;
    const matchesService = filterService === 'all' || contact.service === filterService;
    return matchesSearch && matchesStatus && matchesService;
  });

  // Bulk action functions
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) 
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
      setSelectAll(true);
    }
  };

  const handleBulkStatusUpdate = async (status) => {
    if (selectedContacts.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await contactAPI.bulkUpdateStatus(selectedContacts, status);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedContacts.length} contacts to ${status} status`);
        setSelectedContacts([]);
        setSelectAll(false);
        await fetchContacts();
      } else {
        notify.error('Failed to update contacts: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating contacts:', error);
      notify.error('Error updating contacts: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkPriorityUpdate = async (priority) => {
    if (selectedContacts.length === 0) return;
    
    try {
      setBulkActionLoading(true);
      const response = await contactAPI.bulkUpdatePriority(selectedContacts, priority);
      
      if (response.data.success) {
        notify.success(`Successfully updated ${selectedContacts.length} contacts to ${priority} priority`);
        setSelectedContacts([]);
        setSelectAll(false);
        await fetchContacts();
      } else {
        notify.error('Failed to update contacts: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating contacts:', error);
      notify.error('Error updating contacts: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedContacts.length} contacts? This action cannot be undone.`)) {
      return;
    }
    
    try {
      setBulkActionLoading(true);
      const response = await contactAPI.bulkDelete(selectedContacts);
      
      if (response.data.success) {
        notify.success(`Successfully deleted ${selectedContacts.length} contacts`);
        setSelectedContacts([]);
        setSelectAll(false);
        await fetchContacts();
      } else {
        notify.error('Failed to delete contacts: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting contacts:', error);
      notify.error('Error deleting contacts: ' + (error.response?.data?.message || error.message || 'Unknown error'));
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Control bulk actions bar visibility
  useEffect(() => {
    setShowBulkActions(selectedContacts.length > 0);
  }, [selectedContacts]);

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center">
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 mr-2" />
              Contacts Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              Manage customer inquiries and leads
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 sm:p-6 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Contacts</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalContacts}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
              <AlertCircle className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">New Inquiries</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {contacts.filter(c => c.status === 'new').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/20 rounded-lg">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">In Progress</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {contacts.filter(c => c.status === 'in_progress').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 dark:bg-green-900/20 rounded-lg">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
            </div>
            <div className="ml-4">
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Resolved</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {contacts.filter(c => c.status === 'resolved').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-lg shadow-sm mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="in_progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
            
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Services</option>
              <option value="website">Website</option>
              <option value="mobile_app">Mobile App</option>
              <option value="digital_marketing">Digital Marketing</option>
              <option value="ecommerce">E-commerce</option>
              <option value="consultation">Consultation</option>
            </select>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {showBulkActions && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                {selectedContacts.length} contact{selectedContacts.length !== 1 ? 's' : ''} selected
              </span>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkStatusUpdate('new')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <AlertCircle className="h-3 w-3 mr-1" />}
                  Mark New
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('in_progress')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Clock className="h-3 w-3 mr-1" />}
                  Mark In Progress
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('resolved')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                  Mark Resolved
                </button>
                
                <button
                  onClick={() => handleBulkStatusUpdate('closed')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <XCircle className="h-3 w-3 mr-1" />}
                  Mark Closed
                </button>
                
                <button
                  onClick={() => handleBulkPriorityUpdate('low')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-gray-600 hover:bg-gray-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  Set Low Priority
                </button>
                
                <button
                  onClick={() => handleBulkPriorityUpdate('medium')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-yellow-600 hover:bg-yellow-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  Set Medium Priority
                </button>
                
                <button
                  onClick={() => handleBulkPriorityUpdate('high')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-orange-600 hover:bg-orange-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  Set High Priority
                </button>
                
                <button
                  onClick={() => handleBulkPriorityUpdate('urgent')}
                  disabled={bulkActionLoading}
                  className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
                >
                  {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                  Set Urgent Priority
                </button>
              </div>
            </div>
            
            <button
              onClick={handleBulkDelete}
              disabled={bulkActionLoading}
              className="px-3 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center disabled:opacity-50"
            >
              {bulkActionLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Delete Selected
            </button>
          </div>
        </div>
      )}

      {/* Contacts Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    {selectAll ? <CheckSquare className="h-4 w-4 mr-2" /> : <Square className="h-4 w-4 mr-2" />}
                    Select All
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 sm:h-6 sm:w-6 border-b-2 border-blue-600"></div>
                    </div>
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-red-600 dark:text-red-400">
                    {error}
                  </td>
                </tr>
              ) : filteredContacts.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    No contacts found
                  </td>
                </tr>
              ) : (
                filteredContacts.map((contact) => {
                  const safeStatus = contact.status && ['new','in_progress','resolved','closed'].includes(contact.status) ? contact.status : 'new';
                  return (
                    <tr key={contact.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleSelectContact(contact.id)}
                          className="flex items-center hover:text-blue-600 dark:hover:text-blue-400"
                        >
                          {selectedContacts.includes(contact.id) ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              {contact.name?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {contact.name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {contact.email}
                            </div>
                            {contact.company && (
                              <div className="text-xs text-gray-400 dark:text-gray-500">
                                {contact.company}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white capitalize">
                          {contact.service.replace('_', ' ')}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 dark:text-white max-w-xs truncate">
                          {contact.subject}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getStatusBadge(safeStatus)}>
                          {safeStatus.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={getPriorityBadge(contact.priority)}>
                          {contact.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {new Date(contact.created_at).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => {
                              setSelectedContact(contact);
                              setShowViewModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <select
                            value={safeStatus}
                            onChange={(e) => handleStatusUpdate(contact.id, e.target.value)}
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          >
                            <option value="new">New</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                          <select
                            value={contact.priority}
                            onChange={(e) => handlePriorityUpdate(contact.id, e.target.value)}
                            className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                          >
                            <option value="low">Low</option>
                            <option value="medium">Medium</option>
                            <option value="high">High</option>
                          </select>
                          <button
                            onClick={() => handleDeleteContact(contact.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* View Contact Modal */}
      {showViewModal && selectedContact && (
        <ContactViewModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false);
            setSelectedContact(null);
          }}
          contact={selectedContact}
        />
      )}
    </div>
  );
};

// Contact View Modal Component
const ContactViewModal = ({ isOpen, onClose, contact }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Contact Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <XCircle className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>
        
        <div className="space-y-6">
          {/* Contact Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <p className="text-sm text-gray-900 dark:text-white">{contact.name}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email
              </label>
              <p className="text-sm text-gray-900 dark:text-white">{contact.email}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Phone
              </label>
              <p className="text-sm text-gray-900 dark:text-white">{contact.phone}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Company
              </label>
              <p className="text-sm text-gray-900 dark:text-white">{contact.company}</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Service
              </label>
              <p className="text-sm text-gray-900 dark:text-white capitalize">
                {contact.service.replace('_', ' ')}
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Date
              </label>
              <p className="text-sm text-gray-900 dark:text-white">
                {new Date(contact.created_at).toLocaleString()}
              </p>
            </div>
          </div>
          
          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Subject
            </label>
            <p className="text-sm text-gray-900 dark:text-white">{contact.subject}</p>
          </div>
          
          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Message
            </label>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                {contact.message}
              </p>
            </div>
          </div>
          
          {/* Status and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                contact.status === 'new' 
                  ? 'text-blue-700 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20'
                  : contact.status === 'in_progress'
                  ? 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20'
                  : contact.status === 'resolved'
                  ? 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20'
                  : 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20'
              }`}>
                {contact.status.replace('_', ' ')}
              </span>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priority
              </label>
              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                contact.priority === 'high' 
                  ? 'text-red-700 bg-red-100 dark:text-red-400 dark:bg-red-900/20'
                  : contact.priority === 'medium'
                  ? 'text-yellow-700 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20'
                  : 'text-green-700 bg-green-100 dark:text-green-400 dark:bg-green-900/20'
              }`}>
                {contact.priority}
              </span>
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md"
            >
              Close
            </button>
            <button
              onClick={() => window.open(`mailto:${contact.email}?subject=Re: ${contact.subject}`)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md flex items-center"
            >
              <Reply className="h-4 w-4 mr-2" />
              Reply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactsManagement;
